import { createClient } from '@supabase/supabase-js';

/*
 * ---------------------------------------------------------
 * SUPABASE ENVIRONMENT CONFIGURATION
 * ---------------------------------------------------------
 *
 * MyPiggy must receive its Supabase project configuration
 * from environment variables.
 *
 * No development fallback values are intentionally provided.
 * This prevents Production, Deploy Previews, or future mobile
 * builds from silently connecting to the wrong Supabase project.
 */

const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL;

const SUPABASE_ANON_KEY =
  import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    'Missing required Supabase environment variables: ' +
      'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  );
}

/*
 * ---------------------------------------------------------
 * PARENT CLIENT
 * ---------------------------------------------------------
 *
 * Parent authentication supports:
 * - Email/password
 * - Email confirmation
 * - Password recovery
 * - Future Google login
 * - Future Apple login
 *
 * URL session detection remains enabled because Supabase
 * authentication links return through the browser URL.
 */

export const supabaseParent = createClient(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }
);

/*
 * ---------------------------------------------------------
 * KID CLIENT
 * ---------------------------------------------------------
 *
 * Kid authentication intentionally uses a different browser
 * storage key so a kid login does not overwrite a parent's
 * active session on the same browser.
 */

export const supabaseKid = createClient(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      storageKey: 'sb-kid-auth',
    },
  }
);

/*
 * ---------------------------------------------------------
 * ADMIN CLIENT
 * ---------------------------------------------------------
 *
 * Administrator authentication is isolated from Parent and
 * Kid sessions. The separate storage key prevents an admin
 * login from entering either consumer authentication flow.
 */

export const supabaseAdmin = createClient(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      storageKey: 'sb-admin-auth',
    },
  }
);

/*
 * Existing application code expects the default client to
 * represent the parent Supabase session.
 */

export default supabaseParent;